import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const normalizeErrors = (message) =>
  Array.isArray(message) ? message : message ? [message] : [];

const formatResponsible = (r) => ({
  ...r,
  _id: r._id || r.id
});

const initialState = {
  all_responsibles: [],
  fetchLoading: false,
  loading: false,
  hasFetched: false,
  errors: []
};

export const getAllResponsibles = createAsyncThunk(
  'responsible/getAllResponsibles',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get('/responsible/responsibles');
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const addResponsible = createAsyncThunk(
  'responsible/addResponsible',
  async ({ email, phone, password, firstname, lastname, services }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post('/responsible', {
        email, phone, password, firstname, lastname, services
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteResponsible = createAsyncThunk(
  'responsible/deleteResponsible',
  async ({ responsibleId }, { rejectWithValue, dispatch }) => {
    try {
      await api.delete(`/responsible/${responsibleId}`);
      return responsibleId;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

export const blockResponsible = createAsyncThunk(
  'responsible/blockResponsible',
  async ({ responsibleId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`/responsible/block/${responsibleId}`);
      return { responsibleId, locked: response.data?.data?.locked };
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      if (!error.response) throw error;
      return rejectWithValue(error.response.data);
    }
  }
);

const responsibleSlice = createSlice({
  name: 'responsible',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllResponsibles.pending, (state) => {
        state.fetchLoading = true;
      })
      .addCase(getAllResponsibles.fulfilled, (state, { payload }) => {
        state.all_responsibles = (payload || []).map(formatResponsible);
        state.fetchLoading = false;
        state.hasFetched = true;
      })
      .addCase(getAllResponsibles.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.fetchLoading = false;
      })

      .addCase(addResponsible.pending, (state) => {
        state.loading = true;
      })
      .addCase(addResponsible.fulfilled, (state, { payload }) => {
        state.all_responsibles.push(formatResponsible(payload));
        state.loading = false;
      })
      .addCase(addResponsible.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(deleteResponsible.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteResponsible.fulfilled, (state, { payload: responsibleId }) => {
        state.all_responsibles = state.all_responsibles.filter(
          (r) => r._id !== responsibleId
        );
        state.loading = false;
      })
      .addCase(deleteResponsible.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      })

      .addCase(blockResponsible.pending, (state) => {
        state.loading = true;
      })
      .addCase(blockResponsible.fulfilled, (state, { payload }) => {
        const { responsibleId, locked } = payload;
        const r = state.all_responsibles.find((x) => x._id === responsibleId);
        if (r) {
          r.locked = locked !== undefined ? locked : !r.locked;
        }
        state.loading = false;
      })
      .addCase(blockResponsible.rejected, (state, { payload }) => {
        state.errors = normalizeErrors(payload?.message);
        state.loading = false;
      });
  }
});

export default responsibleSlice.reducer;
